import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Map as MapIcon, ArrowRight } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { SquareLogo, FrameCorners, KnotDivider } from "@/components/ornaments";
import { SeriesShowcase } from "@/components/BookCover";
import { ArticleCard } from "@/components/ArticleCard";
import { templateOf } from "@/lib/templates";
import { WanderButton } from "@/components/WanderButton";
import { FramedImage } from "@/components/FramedImage";
import { useT } from "@/lib/labels";
import { CustomButtons, CustomBlocks } from "@/components/CustomContent";
import { NewsletterBox } from "@/components/Newsletter";

const Hero = ({ settings, t }) => (
  <section className="max-w-7xl mx-auto px-6 sm:px-8 pt-14 pb-20 grid lg:grid-cols-12 gap-12 items-center">
    <div className="lg:col-span-5 rise">
      <SquareLogo src={settings.logo_url} size="lg" testid="hero-square-logo" />
      {t("home.eyebrow") && <p className="eyebrow mt-8">{t("home.eyebrow")}</p>}
      <h1 data-testid="hero-title" className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-3 text-[#2d1b0e]">{settings.site_title}</h1>
      <p className="mt-4 text-xl italic text-[#991b1b]">{settings.tagline}</p>
      <p className="mt-6 text-lg leading-relaxed text-[#3b2c1f] max-w-xl">{settings.intro}</p>
      <div className="mt-9 flex flex-wrap gap-4">
        {t("home.enter_codex") && <Link to="/codex" data-testid="hero-enter-codex" className="btn-illum theme-flame">{t("home.enter_codex")} <ArrowRight className="w-4 h-4" /></Link>}
        {t("home.wander") && <WanderButton testid="hero-wander-button" label={t("home.wander")} className="btn-ghost-illum" />}
        {t("home.realms") && <Link to="/map" data-testid="hero-explore-map" className="btn-ghost-illum"><MapIcon className="w-4 h-4" /> {t("home.realms")}</Link>}
        <CustomButtons place="hero" className="btn-ghost-illum" />
      </div>
    </div>
    <div className="lg:col-span-7 relative rise" style={{ animationDelay: "160ms" }}>
      <div className="relative p-3 vellum gilt">
        <FrameCorners />
        {settings.hero_image && <FramedImage src={settings.hero_image} alt="Illuminated frontispiece" aspect={settings.home_hero_aspect} fallback="classic" />}
      </div>
    </div>
  </section>
);

const SectionTile = ({ s, i }) => {
  const t = useT();
  const Icon = templateOf(s.template).icon;
  const wide = i % 5 === 0;
  return (
    <Link to={`/section/${s.slug}`} data-testid={`home-section-${s.slug}`} style={{ animationDelay: `${i * 70}ms` }}
      className={`group vellum rise lift p-7 flex flex-col theme-${s.theme} ${wide ? "md:col-span-2" : ""}`}>
      <div className="flex items-start justify-between">
        {s.icon_image
          ? <img src={imgUrl(s.icon_image)} alt="" className="w-16 h-16 object-cover border-2 border-[#c9a227]" />
          : <span className="w-14 h-14 grid place-items-center border-2 border-[#c9a227] bg-[color:var(--accent)] text-[#fdf6e3]"><Icon className="w-6 h-6" /></span>}
        <span className="font-heading text-xs tracking-[0.2em] text-[#5e4a35]">{s.article_count} {s.article_count === 1 ? "page" : "pages"}</span>
      </div>
      <h3 className="font-heading text-xl sm:text-2xl font-bold mt-6 text-[#2d1b0e] group-hover:text-[color:var(--accent)] transition-colors">{s.name}</h3>
      <p className="mt-2 text-[#4a3a2a] leading-relaxed">{s.description}</p>
      {t("home.open") && <span className="mt-auto pt-5 inline-flex items-center gap-1 font-heading text-[11px] uppercase tracking-[0.2em] text-[color:var(--accent)]">{t("home.open")} <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-1" /></span>}
    </Link>
  );
};

const Heading = ({ eyebrow, title, id }) => (
  <div id={id} className="mb-10 scroll-mt-28">
    {eyebrow && <p className="eyebrow">{eyebrow}</p>}
    {title && <h2 className="font-display text-3xl sm:text-4xl mt-2 text-[#2d1b0e]">{title}</h2>}
  </div>
);

export default function Home() {
  const { settings, sections, series } = useSite();
  const t = useT();
  const [recent, setRecent] = useState([]);
  useEffect(() => { api.get("/articles", { params: { sort: "recent", limit: 6 } }).then((r) => setRecent(r.data.items)); }, []);
  const secName = Object.fromEntries(sections.map((s) => [s.id, s.name]));

  return (
    <div data-testid="home-page">
      <Hero settings={settings} t={t} />
      <CustomBlocks position="top" />
      {series.length > 0 && (
        <section id="chronicles" className="max-w-7xl mx-auto px-6 sm:px-8 py-16">
          <KnotDivider className="mb-14" />
          <Heading eyebrow={t("home.books_eyebrow")} title={t("home.books_title")} />
          <div className="space-y-20">{series.map((s) => <SeriesShowcase key={s.id} series={s} />)}</div>
        </section>
      )}
      <CustomBlocks position="after_books" />
      <section className="max-w-7xl mx-auto px-6 sm:px-8 py-16">
        <KnotDivider className="mb-14" />
        <Heading eyebrow={t("home.archive_eyebrow")} title={t("home.archive_title")} />
        <div className="grid md:grid-cols-3 gap-6">{sections.map((s, i) => <SectionTile key={s.id} s={s} i={i} />)}</div>
      </section>
      <CustomBlocks position="after_sections" />
      {settings.map_image && (
        <section className="max-w-7xl mx-auto px-6 sm:px-8 py-16">
          <KnotDivider className="mb-14" />
          <div className="grid md:grid-cols-12 gap-10 items-center">
            <Link to="/map" data-testid="home-map-teaser" className="md:col-span-5 group block relative vellum gilt p-3 overflow-hidden lift">
              <FrameCorners />
              <FramedImage src={settings.map_image} alt="Map" aspect={settings.map_aspect} fallback="portrait" imgClassName="transition-transform duration-[1.5s] group-hover:scale-[1.03]" />
            </Link>
            <div className="md:col-span-7">
              {t("home.map_eyebrow") && <p className="eyebrow theme-frost">{t("home.map_eyebrow")}</p>}
              <h2 className="font-display text-3xl sm:text-4xl mt-2">{settings.map_caption || "Map of the Realms"}</h2>
              <p className="mt-4 text-lg leading-relaxed text-[#3b2c1f] max-w-xl">From the frozen marches of the north to the ember-lands of the south — trace the borders of every kingdom and open its chronicle.</p>
              {t("home.map_cta") && <Link to="/map" data-testid="home-map-cta" className="btn-illum theme-frost mt-8">{t("home.map_cta")} <ArrowRight className="w-4 h-4" /></Link>}
            </div>
          </div>
        </section>
      )}
      <CustomBlocks position="after_map" />
      {recent.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 sm:px-8 py-16">
          <Heading eyebrow={t("home.recent_eyebrow")} title={t("home.recent_title")} />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{recent.map((a, i) => <ArticleCard key={a.id} a={a} index={i} sectionName={secName[a.section_id]} />)}</div>
        </section>
      )}
      <CustomBlocks position="after_recent" />
      <NewsletterBox variant="home" />
    </div>
  );
}
